import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { filter, first, find, isEmpty, isNumber } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { CircleNotchIcon } from '@revfluence/fresh-icons/regular/esm';
import { Check, X } from 'lucide-react';

import {
  TAutomationActionOption,
  useGetMessageTemplateById,
  MessageTemplate,
  MessageTemplateInput,
  useGetInstalledApplicationIds,
} from '@frontend/app/hooks';
import { TemplateTransportType, TemplateBodyType, TemplateType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { MessageTemplateEditor } from '@frontend/app/containers/Projects/AutomationConfig/ConfigDrawer/MessageTemplateEditor';
import { useAuth } from '@frontend/context/authContext';
import { PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { MagnifyingGlassIcon } from '@revfluence/fresh-icons/regular/esm';
import { Card } from '@frontend/shadcn/components/ui/card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faArrowUpRightFromSquare, faPlus, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { GMAIL_APP_ID, OUTLOOK_APP_ID } from '@frontend/app/constants/applicationIds';
import { AutomationBlueprintName } from '@frontend/app/containers/Projects/AutomationConfig/types';
import { useAppContext } from '@frontend/context/AppContext';
import { ApolloProvider } from '@apollo/client';
import { AutomationNodeV2 } from '../../types';
import { Button } from '@/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Label } from '@/shadcn/components/ui/label';
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { TemplateHoverPreview } from './TemplateHoverPreview';
import { CatalogSelectionSectionV2 } from './CatalogSelectionSectionV2';

interface SendEmailActionFormProps {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  actionOptions: TAutomationActionOption[];
  onUpdateMessageTemplate: (updatedTemplate: MessageTemplate | MessageTemplateInput) => void;
  resetMessageTemplate: boolean;
  onSave: () => void;
  projectId?: number;
  blueprintName?: string;
}

export const SendEmailActionForm: React.FC<SendEmailActionFormProps> = ({
  selectedNode,
  onUpdateNode,
  actionOptions,
  onUpdateMessageTemplate,
  resetMessageTemplate,
  onSave,
  projectId,
  blueprintName,
}) => {
  const { user, clientInfo } = useAuth();
  const { pfaApolloClient } = useAppContext();
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [resourceId, setResourceId] = useState<number>();
  const [fromPreviousSender, setFromPreviousSender] = useState<boolean>();
  const [templateValidationError, setTemplateValidationError] = useState<string>('');
  const [resourceValidationError, setResourceValidationError] = useState<string>('');
  const [newTemplate, setNewTemplate] = useState<MessageTemplateInput | null>(null);
  const [createNewTemplateDisabled, setCreateNewTemplateDisabled] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCatalog, setSelectedCatalog] = useState<number | undefined>(selectedNode.metadata.brandCatalogId);
  const [selectedRule, setSelectedRule] = useState<number | undefined>(selectedNode.metadata.selectionRuleId);
  const [catalogValidationError, setCatalogValidationError] = useState<string>('');
  const [ruleValidationError, setRuleValidationError] = useState<string>('');
  const [availableCriteria, setAvailableCriteria] = useState<any[]>([]);

  // Memoize the criteria change callback to prevent infinite re-renders
  const handleCriteriaChange = useCallback((criteria: any[]) => {
    setAvailableCriteria(criteria);
  }, []);

  // Custom dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const installedApps = useGetInstalledApplicationIds();
  const emailAppId = useMemo(() => (
    installedApps[GMAIL_APP_ID] ? GMAIL_APP_ID : OUTLOOK_APP_ID
  ), [installedApps]);
  const resources = useMemo(() => (
    actionOptions && actionOptions[0]?.emailResources ? actionOptions[0].emailResources.filter(
      (resource) => (
        resource.userId === user.sub
        || resource.id === resourceId
        || resource.displayName === 'Previous Sender'
      ),
    ) : []
  ), [actionOptions, user.sub, resourceId]);
  const selectedResourceExist = useMemo(() => !isEmpty(find(resources, { id: resourceId })), [resources, resourceId]);

  const filteredTemplates = useMemo(() => {
    const templates = actionOptions[0]?.messageTemplates || [];
    if (!searchTerm.trim()) return templates;
    return templates.filter((template) =>
      template.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [actionOptions, searchTerm]);

  // Initialize state from selectedNode metadata
  useEffect(() => {
    if (selectedNode && selectedNode.type === AutomationNodeType.ACTION) {
      setTemplateId(selectedNode.metadata.templateId);
      setResourceId(selectedNode.metadata.resourceId);
      setFromPreviousSender(selectedNode.metadata.fromPreviousSender);
    }
  }, [selectedNode]);

  useEffect(() => {
    if (resetMessageTemplate) {
      setCreateNewTemplateDisabled(false);
      setNewTemplate(null);
    }
  }, [resetMessageTemplate]);

  const actionNodeDefaultTemplateData = useMemo(() => {
    if (!actionOptions || !actionOptions[0]?.messageTemplates) return null;
    const filtered = filter(
      actionOptions[0].messageTemplates,
      (template) => template.messageTemplateType !== TemplateType.GENERIC,
    );
    return first(filtered);
  }, [actionOptions]);

  const {
    data: {
      template: defaultAutomationTemplate = null,
    } = {},
  } = useGetMessageTemplateById({
    variables: {
      templateId: actionNodeDefaultTemplateData?.id,
    },
    skip: !actionNodeDefaultTemplateData?.id,
  });

  const {
    data: {
      template = null,
    } = {},
    loading: isTemplateLoading,
  } = useGetMessageTemplateById({
    variables: {
      templateId,
    },
    skip: !templateId || templateId === 1, // Skip if no templateId or if it's the default value 1
  });

  // Simple useState like the old working code
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | MessageTemplateInput | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // Temporary state to track original template for Cancel functionality
  const [originalTemplate, setOriginalTemplate] = useState<MessageTemplate | MessageTemplateInput | null>(null);

  // Simple useEffect to update selectedTemplate - following old working pattern
  useEffect(() => {
    const newSelectedTemplate = template || newTemplate;
    setSelectedTemplate(newSelectedTemplate);
  }, [template, newTemplate]);

  const handleCreateNewTemplate = () => {
    setCreateNewTemplateDisabled(true);
    setTemplateId(null);
    const newTpl = {
      id: uuidv4(),
      title: 'New Template',
      subject: 'Enter the subject here',
      text: defaultAutomationTemplate?.text,
      transportType: TemplateTransportType.EMAIL,
      bodyType: TemplateBodyType.HTML,
      type: TemplateType.GENERIC,
    };
    setNewTemplate(newTpl);
    // Store original template for Cancel functionality
    setOriginalTemplate({ ...newTpl });
    setIsEditing(true); // Auto-open editor for new templates
  };

  const handleTemplateChange = (templateId: number | null) => {
    setCreateNewTemplateDisabled(false);
    setTemplateId(templateId);
    setNewTemplate(null); // Clear new template when selecting existing one
    setOriginalTemplate(null); // Clear original template
    setIsEditing(false); // Reset editing state when selecting existing template
  };

  const validateAction = () => {
    const noTemplateError = 'Please select an email template';
    const noResourceError = 'Please select an email';
    const noCatalogError = 'Please select a catalog';
    const noRuleError = 'Please select a rule';
    const noError = '';

    const templateError = (!templateId && !newTemplate) ? noTemplateError : noError;
    const resourceError = (!resourceId && !fromPreviousSender) || !selectedResourceExist ? noResourceError : noError;

    setTemplateValidationError(templateError);
    setResourceValidationError(resourceError);

    // Add validation for catalog and rule if needed for product order blueprint
    if (blueprintName === AutomationBlueprintName.SendProductOrderRequestAfterBriefIsAccepted) {
      const catalogError = !selectedCatalog ? noCatalogError : noError;
      // Only validate rule if catalog is selected AND there are rules available
      const ruleError = (selectedCatalog && availableCriteria.length > 0 && !selectedRule) ? noRuleError : noError;

      setCatalogValidationError(catalogError);
      setRuleValidationError(ruleError);

      return templateError === noError &&
        resourceError === noError &&
        catalogError === noError &&
        ruleError === noError;
    }

    return templateError === noError && resourceError === noError;
  };

  const handleSave = () => {
    if (!validateAction()) return;

    // Update the node metadata with the form values
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        templateId,
        resourceId,
        fromPreviousSender,
        brandCatalogId: selectedCatalog,
        selectionRuleId: selectedRule,
      },
    });

    onSave();
  };

  const onEmailResourceUpdate = (resourceId: number, resource: any) => {
    setResourceId(resourceId);
    setFromPreviousSender(resource.data.fromPreviousSender);
  };

  // Handler to change template (go back to empty state)
  const handleChangeTemplate = () => {
    // Clear all template-related state
    setTemplateId(null);
    setNewTemplate(null);
    setSelectedTemplate(null);
    setOriginalTemplate(null);
    setCreateNewTemplateDisabled(false);
    setIsEditing(false); // Reset editing state
  };

  // Custom dropdown functions
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 400;

      const spaceBelow = viewportHeight - rect.bottom;
      const positionAbove = spaceBelow < dropdownMaxHeight && rect.top > dropdownMaxHeight;

      setDropdownPosition({
        top: positionAbove
          ? rect.top + window.scrollY - dropdownMaxHeight - 4
          : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const handleDropdownToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!isDropdownOpen) {
      updateDropdownPosition();
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTemplateSelect = (template: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const templateId = typeof template.id === 'string' ? parseInt(template.id, 10) : template.id;
    if (!isNaN(templateId)) {
      handleTemplateChange(templateId);
    }
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside - improved event handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!isDropdownOpen) return;

      const target = event.target as HTMLElement;

      // Check if click is inside dropdown container or portal
      const isInsideDropdown = target.closest('.template-dropdown-container');
      const isInsideDropdownPortal = target.closest('.template-dropdown-portal');
      const isTooltip = target.closest('[role="tooltip"]');
      const isInput = target.tagName.toLowerCase() === 'input';

      // Don't close if clicking inside dropdown, tooltip, or on input elements
      if (isInsideDropdown || isInsideDropdownPortal || isTooltip || isInput) {
        return;
      }

      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('touchstart', handleClickOutside, true);
      };
    }
  }, [isDropdownOpen]);

  const hasValidTemplateId = templateId && templateId !== 1;

  return (
    <div className="h-full flex flex-col">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 overflow-y-auto space-y-4 px-1 pb-20">
        {/* Catalog Selection for Product Order Blueprint */}
        {blueprintName === AutomationBlueprintName.SendProductOrderRequestAfterBriefIsAccepted && projectId && (
          <ApolloProvider client={pfaApolloClient}>
            <CatalogSelectionSectionV2
              selectedCatalog={selectedCatalog}
              setSelectedCatalog={setSelectedCatalog}
              selectedRule={selectedRule}
              setSelectedRule={setSelectedRule}
              projectId={projectId}
              onCriteriaChange={handleCriteriaChange}
            />
          </ApolloProvider>
        )}

        {/* Send email from section */}
        <div className="space-y-2">
          <Label htmlFor="email-resource">Select Email to send</Label>
          <Select
            value={selectedResourceExist ? String(resourceId) : undefined}
            onValueChange={(value) => {
              const resource = resources.find((r) => String(r.id) === value);
              if (resource) {
                onEmailResourceUpdate(resource.id, { data: resource });
              }
            }}
          >
            <SelectTrigger id="email-resource">
              <SelectValue placeholder="Select email resource" />
            </SelectTrigger>
            <SelectContent>
              {resources.map((resource) => (
                <SelectItem key={resource.id} value={String(resource.id)}>
                  {resource.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {resourceValidationError && (
            <p className="text-sm text-red-500">{resourceValidationError}</p>
          )}
        </div>
        {/* Template Preview Card - Show when template is selected but NOT editing */}
        {(hasValidTemplateId || newTemplate) && selectedTemplate && !isEditing && (
          <Card className="relative px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <TooltipProvider>
                  <ShadcnTooltip>
                    <TooltipTrigger asChild>
                      <h3 className="font-medium text-grey-6 m-0 text-xs truncate max-w-[200px]">
                        {'displayName' in selectedTemplate ? selectedTemplate.displayName : selectedTemplate.title || 'Welcome to Ambassador Offer'}
                      </h3>
                    </TooltipTrigger>
                    <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[250px]">
                      {'displayName' in selectedTemplate ? selectedTemplate.displayName : selectedTemplate.title || 'Welcome to Ambassador Offer'}
                    </TooltipContent>
                  </ShadcnTooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-1">
                {hasValidTemplateId && isNumber(templateId) && (
                  <div>
                    <TooltipProvider>
                      <ShadcnTooltip>
                        <TooltipTrigger asChild>
                          <a
                            target="_blank"
                            href={`/client/${clientInfo.id}/settings/messageTemplates?templateId=${templateId}`}
                            className="inline-flex items-center justify-center h-8 w-8 p-0 hover:bg-gray-100 rounded"
                          >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-3 h-3 text-grey-5" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50">
                          Open template in new tab
                        </TooltipContent>
                      </ShadcnTooltip>
                    </TooltipProvider>
                  </div>
                )}
                <TooltipProvider>
                  <ShadcnTooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Store original template for Cancel functionality
                          setOriginalTemplate(selectedTemplate ? { ...selectedTemplate } : null);
                          setIsEditing(true);
                        }}
                        className="h-8 w-8 p-0 hover:bg-gray-100 rounded"
                        aria-label="Edit template"
                      >
                        <FontAwesomeIcon icon={faPencil} className="w-3 h-3 text-grey-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50">
                      Edit
                    </TooltipContent>
                  </ShadcnTooltip>
                </TooltipProvider>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-grey-6 text-xs">Subject: </span>
              <TooltipProvider>
                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs font-normal truncate max-w-[200px] text-grey-6">{selectedTemplate.subject || ''}</span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[250px]">
                    {selectedTemplate.subject || ''}
                  </TooltipContent>
                </ShadcnTooltip>
              </TooltipProvider>
            </div>
          </Card>
        )}

        {/* Template Editor - Replace the card when editing */}
        {selectedTemplate && isEditing && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-grey-6 text-lg">
                {'displayName' in selectedTemplate ? selectedTemplate.displayName : selectedTemplate.title || 'Edit Template'}
              </h3>
            </div>
            <MessageTemplateEditor
              messageTemplate={selectedTemplate}
              onUpdateMessageTemplate={onUpdateMessageTemplate}
              resetMessageTemplate={resetMessageTemplate}
              hideEmailSignature
              hideVariableReplacement
            />
            {/* Save and Cancel buttons at the bottom */}
            <div className="flex items-center gap-2 pt-0">
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  // Same functionality as the old working Back button
                  setIsEditing(false);
                  setOriginalTemplate(null); // Clear original since we're saving
                }}
                className="w-6 h-6 bg-grey-6 hover:bg-grey-6 text-white p-1.5 rounded-lg"
              >
                <Check className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Revert to original template if available
                  if (originalTemplate) {
                    if (newTemplate) {
                      setNewTemplate(originalTemplate as MessageTemplateInput);
                    } else {
                      // For existing templates, we need to notify the parent to revert changes
                      onUpdateMessageTemplate(originalTemplate);
                    }
                    setSelectedTemplate(originalTemplate);
                  }
                  setIsEditing(false);
                  setOriginalTemplate(null);
                }}
                className="w-6 h-6 bg-white hover:bg-white text-grey-6 p-1.5 rounded-lg border-grey-2"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Show Change and Create New buttons when template is selected */}
        {(hasValidTemplateId || newTemplate) && !isEditing && (
          <>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <Button size="smallIcon" variant="outline" onClick={handleChangeTemplate}>
                      <FontAwesomeIcon icon={faArrowsRotate} className="w-3 h-3 text-grey-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50">
                    Change Email Template
                  </TooltipContent>
                </ShadcnTooltip>
              </TooltipProvider>
              <TooltipProvider>
                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <Button size="smallIcon" variant="outline" onClick={handleCreateNewTemplate} disabled={createNewTemplateDisabled}>
                      <FontAwesomeIcon icon={faPlus} className="w-3 h-3 text-grey-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50">
                    Create New Template
                  </TooltipContent>
                </ShadcnTooltip>
              </TooltipProvider>
            </div>
            <div className="flex gap-2 flex-col">
              <span className="text-grey-5 text-xs font-medium">
                Your signature will be included in this template.
              </span>
              <a target="_blank" rel="noopener noreferrer" className="text-grey-6 text-xs font-medium" href={`/client/${clientInfo.id}/settings/${emailAppId}`}>
                Edit Signature <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-3 h-3 text-grey-5" />
              </a>
            </div>
          </>
        )}

        {/* Email Template section */}
        <div className="space-y-4">
          {/* Template Selection Button - Show when no template is selected (templateId === 1 means no template) */}
          {!hasValidTemplateId && !newTemplate && (
            <>
              <div className="relative template-dropdown-container">
                <div ref={buttonRef}>
                  <Button
                    variant="outline"
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 h-14 justify-center hover:border-gray-400 hover:bg-gray-50"
                    onClick={(e) => handleDropdownToggle(e)}
                  >
                    <div className="flex items-center">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Select Email Template
                    </div>
                  </Button>
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-500 text-sm mb-2">Or</div>
                <button
                  onClick={handleCreateNewTemplate}
                  disabled={createNewTemplateDisabled}
                  className="text-sm text-gray-600 underline hover:text-gray-800 disabled:text-gray-400 disabled:no-underline"
                >
                  Create New
                </button>
              </div>
            </>
          )}

          {templateValidationError && (
            <p className="text-sm text-red-500">{templateValidationError}</p>
          )}
          {catalogValidationError && (
            <p className="text-sm text-red-500">{catalogValidationError}</p>
          )}
          {ruleValidationError && (
            <p className="text-sm text-red-500">{ruleValidationError}</p>
          )}
        </div>

        {/* Loading state for template data */}
        {!selectedTemplate && isTemplateLoading && (
          <div className="flex items-center justify-center py-8">
            <CircleNotchIcon className="h-6 w-6 animate-spin" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start p-4 border-t bg-white shadow-lg z-[99]">
        <Button
          variant="outline"
          onClick={handleSave}
        >
          Done
        </Button>
      </div>

      {/* Fixed dropdown for template selection - matching AddToOfferActionForm pattern */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            // Only close if clicking outside the dropdown content
            if (e.target === e.currentTarget) {
              setIsDropdownOpen(false);
            }
          }}
        >
          <div
            className="absolute bg-white rounded-2xl shadow-[0px_8px_24px_8px_rgba(93,105,116,0.05),0px_4px_8px_0px_rgba(93,105,116,0.08),0px_2px_6px_-4px_rgba(0,0,0,0.12)] overflow-hidden template-dropdown-portal"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 10000,
            }}
          >
            <div className="p-2 space-y-2">
              {/* Search input - matching AddToOfferActionForm pattern */}
              <div className="flex items-center gap-2 px-2 h-8 rounded-lg border border-solid border-[#E5E8EB] bg-white">
                <MagnifyingGlassIcon className="w-4 h-4 text-[#A2ADB9] flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search Template"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 text-grey-6 text-sm border-0 outline-none bg-transparent placeholder:text-[#A2ADB9] h-8"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>

              {/* Templates list with scroll */}
              <div className="max-h-[350px] overflow-y-auto">
                {filteredTemplates.length === 0 ? (
                  <div className="p-4 text-center text-[#A2ADB9] text-sm">
                    {searchTerm ? `No templates found matching "${searchTerm}"` : 'No templates available'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredTemplates.map((template) => (
                      <Button
                        key={template.id}
                        variant="ghost"
                        className={`flex items-center gap-2 px-2 py-2 w-full rounded-lg h-auto justify-start ${
                          Number(template.id) === templateId ? 'bg-[#E5E8EB]' : 'hover:bg-[#E5E8EB]'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleTemplateSelect(template, e);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex-1 text-left min-w-0">
                          <div className="font-medium text-grey-6 text-sm truncate max-w-[240px]" title={template.displayName}>
                            {template.displayName}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <TemplateHoverPreview template={template} />
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
